/**
 * HelpBootstrapper — Admin floating panel that AI-generates help text for
 * every `[data-help-control]` discovered in the live DOM and in the
 * cumulative Known Forms Registry (localStorage).
 *
 * Data sources per form:
 *   1. Live DOM — what's currently rendered in the browser
 *   2. Known Forms Registry — localStorage["retailcliq_help_registry"]
 *      populated automatically by HelpProvider every time a form mounts.
 *      This lets admins bulk-generate for forms they have visited at some
 *      point in the session, without having to navigate back to each one.
 *
 * Admin-only (SUPER_ADMIN / sys-admin / superAdmin — hardcoded for now,
 * will switch to MasterDB-driven rules once multi-tenant login lands).
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Wand2, Loader, CheckCircle, X, RefreshCw, AlertCircle, ListChecks, Database, Eye } from 'lucide-react';
import { API_BASE as API_URL, getToken } from '../../lib/api';

const PROJECT = 'RetailERP';
const REG_KEY = 'retailcliq_help_registry';

/** Walk up from an element until we find the form boundary. */
function findForm(el) {
  let cur = el;
  while (cur && cur !== document.body) {
    if (cur.dataset?.helpForm && cur.dataset.helpForm !== '__none__') return cur.dataset.helpForm;
    cur = cur.parentElement;
  }
  // Fallback: legacy data-dev-component
  cur = el;
  while (cur && cur !== document.body) {
    if (cur.dataset?.devComponent) return cur.dataset.devComponent;
    cur = cur.parentElement;
  }
  return null;
}

/** Scan live DOM. */
function scanDom() {
  const byForm = new Map();
  document.querySelectorAll('[data-help-control]').forEach((n) => {
    const id = n.dataset.helpControl;
    if (!id) return;
    const form = findForm(n) || 'Unknown';
    if (!byForm.has(form)) byForm.set(form, new Map());
    const group = byForm.get(form);
    if (group.has(id)) return;
    const label =
      n.getAttribute('aria-label') ||
      (n.previousElementSibling?.textContent || '').trim() ||
      n.closest('label')?.textContent?.trim() ||
      n.placeholder || id;
    group.set(id, (label || id).substring(0, 60));
  });
  return byForm;
}

/** Load Known Forms Registry. */
function loadRegistry() {
  try { return JSON.parse(localStorage.getItem(REG_KEY) || '{}'); } catch { return {}; }
}

function saveRegistry(domMap) {
  const reg = loadRegistry();
  const ts = new Date().toISOString();
  domMap.forEach((group, form) => {
    reg[form] = { controls: Array.from(group.entries()).map(([controlId, controlName]) => ({ controlId, controlName })), lastSeen: ts };
  });
  try { localStorage.setItem(REG_KEY, JSON.stringify(reg)); } catch {}
  return reg;
}

export function HelpBootstrapper({ userRole = '' }) {
  // Resolve role from storage if the prop was empty (hydration race)
  const resolvedRole = (() => {
    if (userRole) return userRole;
    try {
      const raw = localStorage.getItem('retailcliq_user') || sessionStorage.getItem('retailcliq_user') || localStorage.getItem('rcq_user') || sessionStorage.getItem('rcq_user');
      if (raw) {
        const u = JSON.parse(raw);
        return u?.role || u?.user?.role || '';
      }
    } catch (_) { /* silent */ }
    return '';
  })();
  // Read adminFeatures from global context storage
  const adminFeatures = (() => {
    try {
      const raw = localStorage.getItem('rcq_global_context') || sessionStorage.getItem('rcq_global_context') || localStorage.getItem('retailcliq_global_context') || sessionStorage.getItem('retailcliq_global_context');
      if (raw) {
        const ctx = JSON.parse(raw);
        return Array.isArray(ctx?.adminFeatures) ? ctx.adminFeatures : [];
      }
    } catch (_) { /* silent */ }
    return [];
  })();
  const isAdmin =
    resolvedRole === 'SUPER_ADMIN' || resolvedRole === 'sys-admin' || resolvedRole === 'superAdmin' || resolvedRole === 'admin' ||
    ((resolvedRole === 'ADMINSUPPORT' || resolvedRole === 'LICENSOR') && adminFeatures.includes('DYNAMIC_ATTRIBUTES'));

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('registry'); // 'registry' (all known) | 'dom' (current screen only)
  const [scan, setScan] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [progress, setProgress] = useState({});
  const [running, setRunning] = useState(false);

  const rescan = useCallback((whichMode = mode) => {
    const live = scanDom();
    const registry = saveRegistry(live); // merge current DOM into registry

    let groups;
    if (whichMode === 'dom') {
      groups = Array.from(live.entries()).map(([form, m]) => ({
        form,
        source: 'dom',
        controls: Array.from(m.entries()).map(([controlId, controlName]) => ({ controlId, controlName })),
      }));
    } else {
      groups = Object.entries(registry).map(([form, v]) => ({
        form,
        source: live.has(form) ? 'dom+registry' : 'registry',
        lastSeen: v.lastSeen,
        controls: v.controls || [],
      }));
    }
    groups = groups.filter((g) => g.controls.length > 0).sort((a, b) => a.form.localeCompare(b.form));
    setScan(groups);
    setSelected(new Set(groups.map((g) => g.form)));
    setProgress({});
  }, [mode]);

  const openPanel = () => { rescan(); setOpen(true); };
  const switchMode = (m) => { setMode(m); rescan(m); };

  const totals = useMemo(() => {
    let forms = 0, controls = 0;
    for (const g of scan) { forms += 1; controls += g.controls.length; }
    return { forms, controls };
  }, [scan]);

  const runAll = async () => {
    if (running) return;
    setRunning(true);
    const next = {};
    const token = getToken();
    for (const g of scan) {
      if (!selected.has(g.form)) continue;
      next[g.form] = { status: 'running' };
      setProgress({ ...next });
      try {
        const res = await fetch(`${API_URL}/api/help/generate-all`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ project: PROJECT, form: g.form, controls: g.controls }),
        });
        const json = await res.json();
        if (json.success) next[g.form] = { status: 'done', count: json.count || 0 };
        else next[g.form] = { status: 'error', error: json.error || 'Failed' };
      } catch (e) {
        next[g.form] = { status: 'error', error: e.message };
      }
      setProgress({ ...next });
    }
    setRunning(false);
  };

  const toggle = (form) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(form)) n.delete(form); else n.add(form);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === scan.length) setSelected(new Set());
    else setSelected(new Set(scan.map((g) => g.form)));
  };

  const clearRegistry = () => {
    if (!window.confirm('Clear the Known Forms Registry? This only wipes the local cache of visited forms — your saved tooltips are safe.')) return;
    try { localStorage.removeItem(REG_KEY); } catch {}
    rescan();
  };

  if (!isAdmin) return null;

  return (
    <>
      <button
        data-testid="help-bootstrapper-fab"
        onClick={openPanel}
        title="AI: Generate help dictionary"
        style={{
          position: 'fixed', right: 16, bottom: 72, zIndex: 9998,
          width: 44, height: 44, borderRadius: 22,
          background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
          color: 'white', border: 'none', cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(79,70,229,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Wand2 size={18} />
      </button>

      {open && (
        <div
          data-testid="help-bootstrapper-panel"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            zIndex: 99990, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => !running && setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 620, maxHeight: '82vh', background: '#ffffff', color: '#0f172a',
              borderRadius: 12, boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              fontFamily: "'Inter', system-ui, sans-serif",
              border: '1px solid #e2e8f0',
            }}
          >
            {/* Header */}
            <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13 }}>
                <Wand2 size={14} /> Bootstrap Help Dictionary
              </div>
              <button disabled={running} onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#e0e7ff', cursor: running ? 'not-allowed' : 'pointer' }}>
                <X size={16} />
              </button>
            </div>

            {/* Mode switcher */}
            <div style={{ display: 'flex', padding: '10px 16px', gap: 6, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <ModeBtn active={mode === 'registry'} onClick={() => switchMode('registry')} icon={<Database size={11} />} label="All Known Forms" sub="Includes every form you've visited" />
              <ModeBtn active={mode === 'dom'} onClick={() => switchMode('dom')} icon={<Eye size={11} />} label="Current Screen Only" sub="Just what's on screen right now" />
            </div>

            {/* Summary + actions */}
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#475569' }}>
              <div>
                <ListChecks size={11} style={{ display: 'inline', marginRight: 4 }} />
                {totals.forms} form{totals.forms === 1 ? '' : 's'} • {totals.controls} control{totals.controls === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button disabled={running} onClick={() => rescan()} data-testid="rescan-btn" style={ghostBtn}>
                  <RefreshCw size={11} /> Rescan
                </button>
                <button disabled={running} onClick={toggleAll} style={ghostBtn}>
                  {selected.size === scan.length ? 'Deselect All' : 'Select All'}
                </button>
                {mode === 'registry' && (
                  <button disabled={running} onClick={clearRegistry} style={{ ...ghostBtn, color: '#b91c1c' }}>
                    Clear Registry
                  </button>
                )}
              </div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px', fontSize: 12, background: '#ffffff' }}>
              {scan.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: 28 }}>
                  <AlertCircle size={18} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                  No forms discovered in {mode === 'dom' ? 'the current DOM' : 'the registry'}.
                  <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
                    Navigate to a screen with form fields, then click <b>Rescan</b>. The registry grows automatically as you visit screens.
                  </div>
                </div>
              ) : (
                scan.map((g) => {
                  const p = progress[g.form];
                  const checked = selected.has(g.form);
                  return (
                    <div key={g.form} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, minHeight: 40 }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={running}
                        onChange={() => toggle(g.form)}
                        data-testid={`toggle-${g.form}`}
                        style={{ width: 15, height: 15, flexShrink: 0, cursor: running ? 'not-allowed' : 'pointer', accentColor: '#7c3aed' }}
                      />
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', overflow: 'hidden' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.form}</span>
                          <span style={{ flexShrink: 0, fontSize: 9, padding: '1px 6px', borderRadius: 3, background: g.source === 'dom' ? '#dcfce7' : g.source === 'dom+registry' ? '#dbeafe' : '#f1f5f9', color: g.source === 'dom' ? '#166534' : g.source === 'dom+registry' ? '#1e40af' : '#475569', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                            {g.source === 'dom' ? 'Live' : g.source === 'dom+registry' ? 'Live · Cached' : 'Cached'}
                          </span>
                        </div>
                        <div style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.controls.length} control{g.controls.length === 1 ? '' : 's'}{g.lastSeen ? ` • last seen ${new Date(g.lastSeen).toLocaleTimeString()}` : ''}</div>
                      </div>
                      <div style={{ flexShrink: 0, minWidth: 90, textAlign: 'right', fontSize: 11 }}>
                        {p?.status === 'running' && <span style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Loader size={11} style={{ animation: 'spin 0.8s linear infinite' }} />Generating…</span>}
                        {p?.status === 'done' && <span style={{ color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={11} />{p.count} saved</span>}
                        {p?.status === 'error' && <span style={{ color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 4 }} title={p.error}><AlertCircle size={11} />Failed</span>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 10, color: '#64748b' }}>
                Uses Gemini via MASTERDB. Safe to re-run — existing entries are updated, new ones inserted.
              </div>
              <button
                disabled={running || scan.length === 0 || selected.size === 0}
                onClick={runAll}
                data-testid="run-generate-all-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 16px', borderRadius: 6, border: 'none',
                  background: running ? '#94a3b8' : '#7c3aed',
                  color: 'white', cursor: running ? 'not-allowed' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                  boxShadow: running ? 'none' : '0 4px 10px rgba(124,58,237,0.3)',
                }}
              >
                {running ? <Loader size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Wand2 size={12} />}
                {running ? 'Generating…' : `Generate ${selected.size} form${selected.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ModeBtn({ active, onClick, icon, label, sub }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1, padding: '8px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
        textAlign: 'left', background: active ? '#ede9fe' : '#ffffff',
        color: active ? '#5b21b6' : '#334155',
        border: active ? '1px solid #a78bfa' : '1px solid #e2e8f0',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 11 }}>{icon}{label}</div>
      <div style={{ fontSize: 10, color: active ? '#6d28d9' : '#64748b', marginTop: 2 }}>{sub}</div>
    </button>
  );
}

const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', borderRadius: 5, border: '1px solid #e2e8f0',
  background: '#ffffff', color: '#475569', cursor: 'pointer',
  fontSize: 11, fontWeight: 600,
};

export default HelpBootstrapper;
